/*
 * Shared pieces for Settings -> Multi-factor authentication.
 *
 * The factor catalogue is keyed on the service's `name`, never `display_name`:
 * display strings are tenant-editable, so branching on them means a tenant can
 * rename "Passkey" to "Security key" and silently turn this page's WebAuthn
 * flow into an unknown factor with no dialog.
 */
import { BellRing, KeyRound, Smartphone } from "lucide-react";
import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";

// Only the factors this page has a setup flow for. Everything else the tenant
// may offer — SMS, the legacy `webauthn` factor — is filtered out by absence
// rather than by a deny-list, so a factor added server-side later cannot
// appear here with no dialog behind its button.
export const FACTOR_CATALOGUE = {
  totp: {
    key: "totp",
    title: "Authenticator app (TOTP)",
    blurb: "Six-digit codes from an app on your phone.",
    icon: Smartphone,
  },
  passkey: {
    key: "passkey",
    title: "Passkey",
    blurb: "A fingerprint, face, screen lock, or security key on your device.",
    icon: KeyRound,
  },
  // The AuthNull Authenticator. Set up by scanning a QR code with the app
  // (mfa/push/selfEnroll); `enrolled` means this account has at least one
  // phone. "Update" adds another phone — phones are removed from the app's
  // Settings › Devices, never here.
  push: {
    key: "push",
    title: "AuthNull Authenticator",
    blurb: "Approve sign-ins with a push notification on your phone.",
    icon: BellRing,
    updateLabel: "Add phone",
  },
};

export const factorMeta = (name) => FACTOR_CATALOGUE[String(name ?? "").trim().toLowerCase()] || null;

/** "18 Aug 2026" — the enrolment date, without a time the user cannot act on. */
export const fmtDay = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export const btn = (variant) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
  padding: "7px 13px", fontFamily: AN_FONT, fontSize: 12.5, fontWeight: 500,
  borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap",
  color: variant === "primary" ? "#fff" : AN_COLORS.ink2,
  background: variant === "primary" ? AN_COLORS.primary : AN_COLORS.surface,
  border: variant === "primary" ? "none" : `1px solid ${AN_COLORS.line}`,
});

const TONES = {
  info: { bg: AN_COLORS.primaryL, bd: "var(--an-infoBd)", fg: AN_COLORS.ink2 },
  warn: { bg: "#FFF8EC", bd: "#F3E2C0", fg: AN_COLORS.ink2 },
  danger: { bg: "var(--an-dangerBg)", bd: "var(--an-dangerBd)", fg: AN_COLORS.danger },
};

export function Callout({ tone = "info", icon, children }) {
  const t = TONES[tone] || TONES.info;
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 13px",
      background: t.bg, border: `1px solid ${t.bd}`, borderRadius: 8,
      fontFamily: AN_FONT, fontSize: 12.5, lineHeight: 1.55, color: t.fg,
    }}>
      {icon && <span style={{ flexShrink: 0, marginTop: 1 }}>{icon}</span>}
      <span>{children}</span>
    </div>
  );
}
